import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { INVESTMENTS } from "../mock";
import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { getIcon } from "../lib/icons";
import { submitMatchRequest } from "../lib/api";

const RANGES = ["£1k – £10k", "£10k – £50k", "£50k – £250k", "£250k+"];
const HORIZONS = ["< 3 years", "3 – 5 years", "5 – 10 years", "10+ years"];
const GOALS = ["Monthly income", "Capital growth", "Tax efficiency", "Retirement planning"];

export default function Apply() {
  const [params] = useSearchParams();
  const preTypes = (params.get("type") || params.get("types") || "")
    .split(",")
    .filter(Boolean);
  const preSkipStep1 = preTypes.length > 0;

  const [step, setStep] = useState(preSkipStep1 ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    types: preTypes,
    range: "",
    horizon: "",
    goal: "",
    name: "",
    email: "",
    phone: "",
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  const toggleType = (slug) => {
    setData((d) =>
      d.types.includes(slug)
        ? { ...d, types: d.types.filter((s) => s !== slug) }
        : { ...d, types: [...d.types, slug] }
    );
  };

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => (step === 1 ? navigate(-1) : setStep((s) => s - 1));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitMatchRequest(data);
      toast({
        title: "Match request received",
        description: "An investor specialist will be in touch within 24 hours.",
      });
      setStep(5);
    } catch (err) {
      // Network/server failure — gracefully fall back to localStorage so the
      // user isn't blocked.
      try {
        const all = JSON.parse(localStorage.getItem("ihub_submissions") || "[]");
        all.push({ ...data, at: new Date().toISOString(), fallback: true });
        localStorage.setItem("ihub_submissions", JSON.stringify(all));
      } catch (_) { /* ignore */ }
      toast({
        title: "We've got your request",
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          "Saved locally — we'll sync as soon as we're back online.",
      });
      setStep(5);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = {
    1: data.types.length > 0,
    2: data.range && data.horizon,
    3: data.goal,
    4: data.name && data.email && data.phone,
  }[step];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <section className="flex-1 bg-[#f7faf9] py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <button
            onClick={back}
            className="inline-flex items-center gap-2 text-[13px] text-[#0d1a2e]/60 hover:text-[#0d1a2e] mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {step < 5 && (
            <div className="mb-8">
              <div className="flex items-center justify-between text-[12px] font-semibold text-[#0d1a2e]/55 mb-2">
                <span>Step {step} of 4</span>
                <span>Takes under 60 seconds</span>
              </div>
              <div className="h-2 bg-[#0d1a2e]/8 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#16d196] transition-all duration-500"
                  style={{ width: `${(step / 4) * 100}%` }}
                />
              </div>
            </div>
          )}

          <div className="bg-white border border-[#0d1a2e]/8 rounded-3xl p-7 lg:p-10">
            {step === 1 && (
              <div>
                <h1 className="text-[28px] lg:text-[36px] font-extrabold leading-tight text-[#0d1a2e] tracking-[-0.02em]">
                  Which investments <span className="text-[#16d196]">interest you?</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14.5px]">Pick one or more — we'll narrow it later.</p>
                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  {INVESTMENTS.map((inv) => {
                    const on = data.types.includes(inv.slug);
                    const Icon = getIcon(inv.icon);
                    return (
                      <button
                        key={inv.slug}
                        type="button"
                        onClick={() => toggleType(inv.slug)}
                        className={`text-left p-4 rounded-2xl border-2 transition-all ${
                          on
                            ? "bg-[#e3f7ee] border-[#16d196]"
                            : "bg-white border-[#0d1a2e]/8 hover:border-[#16d196]/50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="w-9 h-9 rounded-lg bg-[#0d1a2e] flex items-center justify-center mb-3">
                              <Icon className="w-4.5 h-4.5 text-[#16d196]" strokeWidth={2.2} />
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#0d1a2e]/55">{inv.category}</div>
                            <div className="text-[14.5px] font-extrabold text-[#0d1a2e] mt-1">{inv.name}</div>
                            <div className="text-[12px] text-[#0d1a2e]/55 mt-0.5">{inv.yieldRange}</div>
                          </div>
                          {on && (
                            <div className="w-6 h-6 rounded-full bg-[#16d196] flex items-center justify-center shrink-0">
                              <Check className="w-3.5 h-3.5 text-[#0d1a2e]" strokeWidth={3} />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h1 className="text-[26px] sm:text-[32px] lg:text-[36px] font-extrabold leading-tight text-[#0d1a2e] tracking-[-0.02em]">
                  Size & <span className="text-[#16d196]">horizon?</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14px] sm:text-[14.5px]">Approximate is fine. Nothing here is binding.</p>

                {data.types.length > 0 && (
                  <div className="mt-5 p-4 bg-[#f7faf9] border border-[#0d1a2e]/8 rounded-2xl">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">
                        Matching against
                      </div>
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-[12px] font-bold text-[#0d1a2e]/65 hover:text-[#16d196]"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {data.types.map((slug) => {
                        const t = INVESTMENTS.find((i) => i.slug === slug);
                        if (!t) return null;
                        return (
                          <button
                            key={slug}
                            type="button"
                            onClick={() => toggleType(slug)}
                            className="group inline-flex items-center gap-1.5 bg-white border border-[#0d1a2e]/15 hover:border-[#16d196] rounded-full pl-3 pr-2 py-1.5 text-[12.5px] font-bold text-[#0d1a2e]"
                          >
                            {t.name}
                            <X className="w-3 h-3 text-[#0d1a2e]/45 group-hover:text-[#16d196]" strokeWidth={3} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-7">
                  <Label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Amount you're considering</Label>
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    {RANGES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setData((d) => ({ ...d, range: r }))}
                        className={`h-13 py-3.5 rounded-xl border-2 text-[14px] font-bold transition-all ${
                          data.range === r
                            ? "bg-[#16d196] text-[#0d1a2e] border-[#16d196]"
                            : "bg-white border-[#0d1a2e]/10 text-[#0d1a2e] hover:border-[#16d196]/50"
                        }`}
                      >{r}</button>
                    ))}
                  </div>
                </div>

                <div className="mt-7">
                  <Label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Time horizon</Label>
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    {HORIZONS.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setData((d) => ({ ...d, horizon: h }))}
                        className={`h-13 py-3.5 rounded-xl border-2 text-[14px] font-bold transition-all ${
                          data.horizon === h
                            ? "bg-[#16d196] text-[#0d1a2e] border-[#16d196]"
                            : "bg-white border-[#0d1a2e]/10 text-[#0d1a2e] hover:border-[#16d196]/50"
                        }`}
                      >{h}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h1 className="text-[28px] lg:text-[36px] font-extrabold leading-tight text-[#0d1a2e] tracking-[-0.02em]">
                  What's your <span className="text-[#16d196]">primary goal?</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14.5px]">Helps us prioritise the right offers.</p>

                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  {GOALS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setData((d) => ({ ...d, goal: g }))}
                      className={`p-5 rounded-2xl border-2 text-left transition-all ${
                        data.goal === g
                          ? "bg-[#16d196] text-[#0d1a2e] border-[#16d196]"
                          : "bg-white border-[#0d1a2e]/10 text-[#0d1a2e] hover:border-[#16d196]/50"
                      }`}
                    >
                      <div className="text-[18px] font-extrabold">{g}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <form onSubmit={submit}>
                <h1 className="text-[28px] lg:text-[36px] font-extrabold leading-tight text-[#0d1a2e] tracking-[-0.02em]">
                  Where shall we send your <span className="text-[#16d196]">match?</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14.5px]">We never share or sell your details. GDPR-secure.</p>

                <div className="mt-7 space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Full name</Label>
                    <Input id="name" required value={data.name} onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))} className="mt-2 h-13 py-3.5 bg-white border-[#0d1a2e]/15 rounded-xl text-[15px]" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Email</Label>
                    <Input id="email" type="email" required value={data.email} onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))} className="mt-2 h-13 py-3.5 bg-white border-[#0d1a2e]/15 rounded-xl text-[15px]" placeholder="jane@email.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">UK phone</Label>
                    <Input id="phone" type="tel" required value={data.phone} onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))} className="mt-2 h-13 py-3.5 bg-white border-[#0d1a2e]/15 rounded-xl text-[15px]" placeholder="07…" />
                  </div>
                  <button
                    type="submit"
                    disabled={!canProceed || submitting}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 py-3.5 text-[15px] font-bold disabled:opacity-50 transition-colors shadow-[0_8px_24px_rgba(22,209,150,0.3)]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send my match
                        <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                      </>
                    )}
                  </button>
                  <p className="text-[11.5px] text-[#0d1a2e]/55 text-center">
                    By submitting you agree to be contacted by Investment Hub UK under our privacy policy. No spam. Ever.
                  </p>
                </div>
              </form>
            )}

            {step === 5 && (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#16d196] flex items-center justify-center shadow-[0_8px_24px_rgba(22,209,150,0.35)]">
                  <Check className="w-8 h-8 text-[#0d1a2e]" strokeWidth={3} />
                </div>
                <h1 className="mt-6 text-[32px] lg:text-[44px] font-extrabold leading-tight text-[#0d1a2e] tracking-[-0.02em]">
                  Thank you, <span className="text-[#16d196]">{data.name.split(" ")[0] || "investor"}.</span>
                </h1>
                <p className="mt-4 text-[#0d1a2e]/65 max-w-md mx-auto text-[15px] leading-relaxed">
                  We've received your match request. An investor specialist will email your
                  personalised offer shortlist within 24 hours.
                </p>
                <div className="mt-8">
                  <Link to="/investments">
                    <button className="inline-flex items-center justify-center gap-1.5 bg-[#0d1a2e] hover:bg-black text-white rounded-full h-13 py-3.5 px-7 text-[15px] font-bold transition-colors">
                      Browse all offers
                      <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {step < 4 && step >= 1 && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={next}
                  disabled={!canProceed}
                  className="inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 py-3.5 px-7 text-[15px] font-bold disabled:opacity-50 transition-colors shadow-[0_8px_24px_rgba(22,209,150,0.3)]"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
